import React from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNowStrict, subDays } from 'date-fns';
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/20/solid';

import { useShop } from '@services/ShopProvider';
import { useEnabledChannels } from '@services/channels';
import { useInsightsDate } from '@services/InsightsDateProvider';
import { getOrderInsights, useOrders } from '@services/orders';

import { JourneyCard } from '../../components/JourneyCard';
import { EmptyState } from '../../components/EmptyState';
import { CARD_STATES, useCardEmptyState } from '../../hooks/useCardEmptyState';

import styles from './OrdersCard.module.scss';

const ORDERS_CHANNEL_IDS = ['orders', 'product-reservations', 'local-checkout'];

// Statuses where the retailer hasn't acted on the order yet — these need attention.
const URGENT_STATUSES = new Set(['placed', 'submitted', 'pending', 'confirmed']);
const isUrgent = (order) => URGENT_STATUSES.has(order.status);

const productLabel = (order) => {
	const first = order.products?.[0];
	if (!first) return `${order.products?.length ?? 0} item${(order.products?.length ?? 0) === 1 ? '' : 's'}`;
	const more = (order.products?.length ?? 0) > 1 ? ` + ${order.products.length - 1} more` : '';
	return `${first.name ?? first.title ?? first.barcode ?? 'Item'}${more}`;
};

const shortId = (order) => order.pickupReference ?? `#${(order.id ?? '').slice(-6)}`;

const customerName = (order) => order.customerName ?? order.contactName ?? 'Customer';

const timeAgo = (timestamp) => {
	if (!timestamp) return null;
	try {
		return formatDistanceToNowStrict(new Date(timestamp), { addSuffix: false });
	} catch {
		return null;
	}
};

/**
 * Orders card — gated to render only when at least one shop has an orders or
 * reservations channel enabled. Shows orderInsights summary (orders count,
 * revenue) plus URGENT and PREVIOUS sections of recent orders.
 *
 * Real fields used: customerName (not contactName), pickupReference for short
 * IDs, products[0].name for the item title, createdAt for time-elapsed pill.
 */
export const OrdersCard = () => {
	const { data: shops, shop } = useShop();
	const enabledChannels = useEnabledChannels() ?? [];
	const ordersChannel = enabledChannels.find((c) => ORDERS_CHANNEL_IDS.includes(c.id));
	const enabledShopIds = new Set(ordersChannel?.enabledShops ?? []);
	const enabledShops = (shops ?? []).filter((s) => enabledShopIds.has(s.id));
	const enabled = enabledShops.length > 0;

	const { startDate, endDate, selectedRange } = useInsightsDate();
	const start = format(startDate, 'yyyy-MM-dd');
	const end = format(endDate, 'yyyy-MM-dd');
	const currency = shops?.[0]?.billingCurrency ?? 'GBP';
	const scopeShopId = shop?.id ?? enabledShops[0]?.id;
	const ordersHref = scopeShopId ? `/orders/${scopeShopId}` : '/orders';

	// Period-scoped: feeds the Orders + Revenue summary cells.
	const insightsKey = enabled ? ['order-insights', shop?.id ?? 'org', start, end, currency] : null;
	const { data: insights, isLoading: insightsLoading } = useSWR(insightsKey, () =>
		getOrderInsights({ shopIds: enabledShops.map((s) => s.id), start, end, currency })
	);

	// Always-on operational window: feeds Urgent + Previous. Bound to 90 days so
	// we don't pull the full history on every load, but unbounded by the page's
	// date picker — a retailer narrowing their analytics view shouldn't hide
	// orders they still need to confirm.
	const operationalEnd = format(new Date(), 'yyyy-MM-dd');
	const operationalStart = format(subDays(new Date(), 90), 'yyyy-MM-dd');
	const { data: orders, isLoading: ordersLoading } = useOrders(
		{ shopIds: enabledShops.map((s) => s.id), start: operationalStart, end: operationalEnd },
		{ suspense: false, refreshInterval: 60000 }
	);

	// Empty-state semantics shift: "no orders this period" no longer makes the
	// card useless — we still surface live urgent items. The card only counts
	// as truly empty when this shop has never received an order in the 90-day
	// operational window AND the period summary is also zero.
	const totalOrders = (insights?.ordersPlaced ?? 0) + (insights?.reservationsPlaced ?? 0);
	const dataIsEmpty = !!insights && totalOrders === 0 && (!orders || orders.length === 0);

	const emptyState = useCardEmptyState({ cardId: 'orders', dataIsEmpty });

	if (!enabled) return null;

	const showLoading = (insightsLoading && !insights) || (ordersLoading && !orders);

	// Sort the whole orders list by createdAt DESC first so the most recent
	// entries float to the top — then split into urgent/previous. Without this
	// the API's old-first ordering meant the freshest orders were never shown.
	const sortedOrders = (orders ?? []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
	const urgent = sortedOrders.filter(isUrgent).slice(0, 2);
	const previous = sortedOrders.filter((o) => !isUrgent(o)).slice(0, 3);
	const urgentCount = sortedOrders.filter(isUrgent).length;
	const subtitle = showLoading
		? ' ' // non-breaking space, so the header keeps its height during load
		: urgentCount > 0
			? `${urgentCount} ${urgentCount === 1 ? 'order needs' : 'orders need'} confirmation`
			: 'No orders awaiting confirmation';

	return (
		<JourneyCard title="Orders" subtitle={subtitle} ctaLabel="Manage orders" ctaHref={ordersHref}>
			{showLoading ? (
				<OrdersCardSkeleton />
			) : emptyState.state !== CARD_STATES.DATA ? (
				<EmptyState state={emptyState.state} copy={emptyState.copy} ctaLabel={emptyState.ctaLabel} ctaHref={emptyState.ctaHref} />
			) : (
				<>
					<div className={styles.summarySection}>
						<h3 className={styles.sectionHeading}>{selectedRange}</h3>
						<div className={styles.summaryRow}>
							<div className={styles.summaryCell}>
								<div className={styles.summaryLabel}>Orders</div>
								<div className={styles.summaryValue}>{insights.ordersPlaced + insights.reservationsPlaced}</div>
							</div>
							<div className={styles.summaryCell}>
								<div className={styles.summaryLabel}>Revenue</div>
								<div className={styles.summaryValue}>{insights.totalSales}</div>
							</div>
						</div>
					</div>

					{urgent.length > 0 && (
						<div className={styles.section}>
							<h3 className={styles.sectionHeading}>Urgent</h3>
							<ul className={styles.list}>
								{urgent.map((o) => {
									const elapsed = timeAgo(o.createdAt);
									return (
										<li key={o.id}>
											<Link to={ordersHref} className={styles.urgentItem}>
												<div className={styles.itemBody}>
													<div className={styles.itemTitle}>{productLabel(o)}</div>
													<div className={styles.itemMeta}>
														{shortId(o)} · {customerName(o)}
													</div>
												</div>
												{elapsed && (
													<span className={styles.urgentBadge}>
														<ClockIcon className={styles.urgentBadgeIcon} aria-hidden="true" />
														<span>{elapsed}</span>
													</span>
												)}
											</Link>
										</li>
									);
								})}
							</ul>
						</div>
					)}

					{previous.length > 0 && (
						<div className={styles.section}>
							<h3 className={styles.sectionHeading}>Previous</h3>
							<ul className={styles.list}>
								{previous.map((o) => (
									<li key={o.id}>
										<Link to={ordersHref} className={styles.recentItem}>
											<span className={styles.recentItemLabel}>
												{shortId(o)} · <span className={styles.muted}>{customerName(o)}</span>
											</span>
											<span className={styles.statusLabel}>{o.status}</span>
											<ArrowRightIcon className={styles.recentItemArrow} aria-hidden="true" />
										</Link>
									</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</JourneyCard>
	);
};

const OrdersCardSkeleton = () => (
	<>
		<div className={styles.summarySection}>
			<div className={`${styles.skeletonBlock} ${styles.skeletonSectionHeading}`} />
			<div className={styles.summaryRow}>
				{[0, 1].map((i) => (
					<div key={i} className={styles.summaryCell}>
						<div className={`${styles.skeletonBlock} ${styles.skeletonSummaryLabel}`} />
						<div className={`${styles.skeletonBlock} ${styles.skeletonSummaryValue}`} />
					</div>
				))}
			</div>
		</div>
		<div className={styles.section}>
			<div className={`${styles.skeletonBlock} ${styles.skeletonSectionHeading}`} />
			<ul className={styles.list}>
				{[0, 1].map((i) => (
					<li key={i} className={styles.urgentItem}>
						<div className={styles.itemBody}>
							<div className={`${styles.skeletonBlock} ${styles.skeletonItemTitle}`} />
							<div className={`${styles.skeletonBlock} ${styles.skeletonItemMeta}`} />
						</div>
						<div className={`${styles.skeletonBlock} ${styles.skeletonUrgentBadge}`} />
					</li>
				))}
			</ul>
		</div>
		<div className={styles.section}>
			<div className={`${styles.skeletonBlock} ${styles.skeletonSectionHeading}`} />
			<ul className={styles.list}>
				{[0, 1, 2].map((i) => (
					<li key={i} className={styles.recentItem}>
						<div className={`${styles.skeletonBlock} ${styles.skeletonRecentLabel}`} />
						<div className={`${styles.skeletonBlock} ${styles.skeletonStatus}`} />
					</li>
				))}
			</ul>
		</div>
	</>
);
